import React, { FC } from 'react'
import { ServicePartner } from './ServicePartnerFinder'

interface PartnerCardProps {
  partner: ServicePartner
  active: boolean
  onClick?: () => void
  id?: string
}

export const PartnerCard: FC<PartnerCardProps> = ({ partner, active, onClick, id }) => {
  return (
    <div
      id={id}
      onClick={onClick}
      className={`flex flex-col rounded-lg border ${active ? 'border-2 border-stroke-signal' : 'border-stroke-default'} bg-white p-6 gap-3 cursor-pointer transition-colors hover:shadow-md`}
    >
      {/* Header: Flexit Partner Badge + Checkmark */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Logo box */}
          <div className="w-[12px] h-[12px] bg-brand-sky-800 relative">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="12" height="12" fill="#23465F" />
              <path
                d="M6.93187 7.9462C6.77857 8.1658 6.77857 8.4577 6.93187 8.6773C7.05127 8.8483 7.24657 8.95 7.45507 8.95H9.04357L7.64227 6.9292L6.93187 7.9462Z"
                fill="#BDDFEF" />
              <path
                d="M7.64227 5.07073L9.04357 3.04993H7.45507C7.24657 3.04993 7.05127 3.15163 6.93187 3.32263C6.77857 3.54223 6.77857 3.83413 6.93187 4.05373L7.64227 5.07073Z"
                fill="#BDDFEF" />
              <path
                d="M4.87774 3.04993H2.95654L5.00224 5.99983L2.95654 8.94973H4.87774L6.93844 5.99983L4.87774 3.04993Z"
                fill="#BDDFEF" />
            </svg>

          </div>
          <span className="text-secondary text-[10px] font-semibold uppercase tracking-[0.13em] leading-none">
            {window.gettext('FLEXIT PARTNER')}
          </span>
        </div>
      </div>

      {/* Partner Name */}
      <div className="flex items-center justify-between">
        <h2 className="text-default text-[15px] font-bold leading-[19px]">
          {partner.name}
        </h2>
        {active && <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2E7ABA] border border-[#2E7ABA]">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 text-secondary text-[13px] font-medium leading-none">
        {partner.is_retailer && (
          <div className="flex items-center bg-accent-olive-subtle rounded-[2px] px-2 py-[5px] h-6">
            {window.gettext('Retailer')}
          </div>
        )}
        {partner.is_service && (
          <div className="flex items-center bg-accent-olive-subtle rounded-[2px] px-2 py-[5px] h-6">
            {window.gettext('Service company')}
          </div>
        )}
        {partner.is_installer && (
          <div className="flex items-center bg-accent-olive-subtle rounded-[2px] px-2 py-[5px] h-6">
            {window.gettext('Flexit partner')}
          </div>
        )}
        {partner.is_econordic_partner && (
          <div className="flex items-center bg-brand-air-100 rounded-[2px] px-2 py-[5px] h-6">
            {window.gettext('EcoNordic partner')}
          </div>
        )}
        {/* Note: 'Ventilation unit' tag was in image but no flag in interface? Assuming handled by flags or missing.
              The 'ServicePartner' interface has: is_retailer, is_installer, is_service, is_econordic_partner.
              I will stick to available flags.
          */}
      </div>

      {/* Separator + Address */}
      <div className="border-t border-stroke-default pt-3 flex flex-col gap-1">
        <div className="text-secondary text-[15px] font-medium leading-[150%]">
          {partner.address_line1 && <div>{partner.address_line1}</div>}
          <div>
            {partner.postal_code} {partner.city}
          </div>
        </div>

        {/* Google Maps Link */}
        <div className="flex items-center gap-1 mt-1">
          {/* Vector Compass Icon */}
          <div className="w-6 h-6 flex items-center justify-center relative">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.0369 4.6879C3.99743 4.59682 3.98626 4.49597 4.00484 4.39846C4.02342 4.30094 4.07088 4.21127 4.14108 4.14108C4.21127 4.07088 4.30094 4.02342 4.39846 4.00484C4.49597 3.98626 4.59682 3.99743 4.6879 4.0369L20.6879 10.5369C20.7852 10.5765 20.8675 10.6458 20.9232 10.7349C20.9789 10.824 21.0051 10.9283 20.9981 11.0331C20.9912 11.138 20.9514 11.2379 20.8844 11.3188C20.8174 11.3997 20.7266 11.4575 20.6249 11.4839L14.5009 13.0639C14.1549 13.1529 13.839 13.3329 13.5861 13.5852C13.3332 13.8376 13.1526 14.1531 13.0629 14.4989L11.4839 20.6249C11.4575 20.7266 11.3997 20.8174 11.3188 20.8844C11.2379 20.9514 11.138 20.9912 11.0331 20.9981C10.9283 21.0051 10.824 20.9789 10.7349 20.9232C10.6458 20.8675 10.5765 20.7852 10.5369 20.6879L4.0369 4.6879Z"
                stroke="#39596F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${partner.name}, ${partner.address_line1}, ${partner.city}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-link text-[15px] font-medium underline leading-[150%]"
          >
            {window.gettext('Open in Google Maps')}
          </a>
        </div>
      </div>

      {/* Separator + Contact Info */}
      <div
        className="border-t border-stroke-default pt-3 gap-x-4 gap-y-1 text-secondary text-[15px] font-medium leading-[150%]">
        {partner.phone && (
          <div><a href={`tel:${partner.phone}`}>
            {partner.phone}
          </a></div>
        )}
        {partner.email && (
          <div><a href={`mailto:${partner.email}`}>
            {partner.email}
          </a></div>
        )}
      </div>

    </div>
  )
}
